import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { AttributeDataType } from '@nucleus-commerce/api-client/admin';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Input } from '@nucleus-commerce/ui/components/input';
import { Switch } from '@nucleus-commerce/ui/components/switch';

import { canBeOption, hasUnit } from '../api/attribute-mappers';
import type { AttributeFormInput } from '../schemas/attribute-form-schema';

type Flag =
  'usableAsOption' | 'isFilterable' | 'isSearchable' | 'isComparable' | 'showOnProductPage';

const FLAGS: readonly Flag[] = [
  'usableAsOption',
  'isFilterable',
  'isSearchable',
  'isComparable',
  'showOnProductPage',
];

/** Only for number and dimension attributes; renders nothing for the other types. */
export function AttributeUnitField({ dataType }: { dataType: AttributeDataType }) {
  const { t } = useTranslation('attributes');
  const { control } = useFormContext<AttributeFormInput>();
  if (!hasUnit(dataType)) return null;
  return (
    <FormField
      control={control}
      name="unit"
      render={({ field }) => (
        <FormItem>
          <FormLabel>{t('fields.unit')}</FormLabel>
          <FormControl>
            <Input maxLength={16} {...field} />
          </FormControl>
          <FormDescription>{t('fields.unitHint')}</FormDescription>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

/** The five switches; "variant option" is off and locked for types that cannot define variants. */
export function AttributeFlagFields({ dataType }: { dataType: AttributeDataType }) {
  const { t } = useTranslation('attributes');
  const { control } = useFormContext<AttributeFormInput>();
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      {FLAGS.map((flag) => {
        const locked = flag === 'usableAsOption' && !canBeOption(dataType);
        return (
          <FormField
            key={flag}
            control={control}
            name={flag}
            render={({ field }) => (
              <FormItem className="flex flex-row items-start justify-between gap-4 rounded-md border p-3">
                <div className="flex flex-col gap-1">
                  <FormLabel>{t(`fields.${flag}`)}</FormLabel>
                  <FormDescription>
                    {locked ? t('fields.usableAsOptionUnavailable') : t(`fields.${flag}Hint`)}
                  </FormDescription>
                </div>
                <FormControl>
                  <Switch
                    checked={!locked && field.value}
                    onCheckedChange={field.onChange}
                    disabled={locked}
                  />
                </FormControl>
              </FormItem>
            )}
          />
        );
      })}
    </div>
  );
}
